import { useEffect, useState, useCallback, type ReactNode, type FormEvent } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';
import { Button } from '../../../../shared/components/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

interface RoiData {
  readonly assessmentId: string;
  readonly reviewId: string;
  readonly jobRequestId: string;
  readonly marketImportance: number;
  readonly customerImportance: number;
  readonly territoryImportance: number;
  readonly salesPotential: number;
  readonly campaignImpact: number;
  readonly strategicImportance: number;
  readonly overallRoiScore: number;
  readonly recommendationCode: string;
  readonly assessmentRemarks: string | null;
  readonly createdAt: string;
}

const LABEL: React.CSSProperties = { fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 2 };

const DIMS = [
  { key: 'marketImportance', label: 'Market Importance' },
  { key: 'customerImportance', label: 'Customer Importance' },
  { key: 'territoryImportance', label: 'Territory Importance' },
  { key: 'salesPotential', label: 'Sales Potential' },
  { key: 'campaignImpact', label: 'Campaign Impact' },
  { key: 'strategicImportance', label: 'Strategic Importance' },
] as const;


const RECOMMENDATIONS = [
  'STRONGLY_RECOMMEND', 'RECOMMEND', 'NEUTRAL', 'NOT_RECOMMEND',
] as const;

export function ShRoiAssessmentPage(): ReactNode {
  const { reviewId } = useParams<{ reviewId: string }>();
  const { token } = useAuth();
  const { t } = useTranslation();
  const { number } = useBusinessNumber();
  // Instants follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();
  const [existing, setExisting] = useState<RoiData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Form state
  const [scores, setScores] = useState<Record<string, number>>({
    marketImportance: 5, customerImportance: 5, territoryImportance: 5,
    salesPotential: 5, campaignImpact: 5, strategicImportance: 5,
  });
  const [remarks, setRemarks] = useState('');
  const [recommendation, setRecommendation] = useState('RECOMMEND');

  const load = useCallback(async () => {
    if (!reviewId) return;
    try {
      const r = await apiRequest<RoiData>(`/v1/sh/review/${encodeURIComponent(reviewId)}/roi`, { token });
      setExisting(r);
      // Pre-fill form with existing
      setScores({
        marketImportance: r.marketImportance,
        customerImportance: r.customerImportance,
        territoryImportance: r.territoryImportance,
        salesPotential: r.salesPotential,
        campaignImpact: r.campaignImpact,
        strategicImportance: r.strategicImportance,
      });
      setRemarks(r.assessmentRemarks ?? '');
      setRecommendation(r.recommendationCode);
    } catch {
      // 404 = no existing assessment, that's fine
      setExisting(null);
    }
  }, [reviewId, token]);

  useEffect(() => { void load(); }, [load]);

  const setScore = (key: string, val: number) => {
    setScores((prev) => ({ ...prev, [key]: Math.max(1, Math.min(10, val)) }));
  };

  const handleSubmit = useCallback(async (e: FormEvent) => {
    e.preventDefault();
    if (!reviewId || busy) return;
    setBusy(true); setError(null); setInfo(null);
    try {
      await apiRequest(`/v1/sh/review/${encodeURIComponent(reviewId)}/roi`, {
        method: 'POST', token,
        body: {
          reviewId, jobRequestId: reviewId, // backend extracts from review
          ...scores,
          assessmentRemarks: remarks || undefined,
          recommendationCode: recommendation,
        },
      });
      setInfo(t('sh.roi.saved'));
      await load();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }, [reviewId, scores, remarks, recommendation, token, busy, load]);

  const avgScore = Object.values(scores).reduce((a, b) => a + b, 0) / Object.values(scores).length;

  return (
    <main aria-label="sh-roi-assessment">
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16 }}>
        <Link to={`/sh/review/${reviewId}`} data-testid="back-review">{t('sh.common.review')}</Link>
        <h1 style={{ fontSize: 20 }}>{t('sh.roi.title')}</h1>
      </div>

      {error && <p role="alert" style={{ color: 'var(--color-danger)', marginBottom: 12 }}>{error}</p>}
      {info && <p role="status" style={{ color: 'var(--color-success)', marginBottom: 12 }}>{info}</p>}

      {existing && (
        <section style={{
          padding: 16, borderRadius: 8, marginBottom: 16,
          border: '1px solid var(--color-border-default)',
          background: 'var(--color-bg-elevated)',
        }} aria-label="existing-roi">
          <h2 style={{ fontSize: 16, marginBottom: 8 }}>{t('sh.roi.currentAssessment')}</h2>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 8 }}>
            <div><strong>{t('sh.roi.overallScore')}</strong> {number(existing.overallRoiScore)}/10</div>
            <div><strong>{t('sh.roi.recommendation')}</strong> {existing.recommendationCode}</div>
            <div><strong>{t('sh.roi.assessed')}</strong> {at(existing.createdAt)}</div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
            {DIMS.map(({ key }) => (
              <div key={key}><span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{t(`sh.roi.dim.${key}`)}:</span> {(existing as any)[key]}/10</div>
            ))}
          </div>
          {existing.assessmentRemarks && <div style={{ marginTop: 8 }}><strong>{t('sh.roi.remarksLabel')}</strong> {existing.assessmentRemarks}</div>}
        </section>
      )}

      {/* ── Assessment form ──────────────────────────── */}
      <section style={{
        padding: 16, borderRadius: 8,
        border: '1px solid var(--color-border-default)',
        background: 'var(--color-bg-elevated)',
      }} aria-label="roi-form">
        <h2 style={{ fontSize: 16, marginBottom: 12 }}>{existing ? t('sh.roi.updateAssessment') : t('sh.roi.newAssessment')}</h2>
        <form onSubmit={handleSubmit} aria-label="roi-submit-form">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 12, marginBottom: 16 }}>
            {DIMS.map(({ key }) => (
              <div key={key}>
                <label htmlFor={`roi-${key}`} style={LABEL}>{t(`sh.roi.dim.${key}`)} (1-10)</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input id={`roi-${key}`} type="range" min={1} max={10} value={scores[key]}
                         onChange={(e) => setScore(key, Number(e.target.value))}
                         data-testid={`slider-${key}`}
                         style={{ flex: 1 }} />
                  <span style={{ fontWeight: 600, minWidth: 24, textAlign: 'center' }}>{scores[key]}</span>
                </div>
              </div>
            ))}
          </div>

          <div style={{ marginBottom: 12, padding: 12, borderRadius: 8, background: avgScore >= 7 ? 'var(--color-success-bg)' : avgScore >= 4 ? 'var(--color-warning-bg)' : 'var(--color-danger-bg)' }}>
            <strong>{t('sh.roi.projectedScore', { score: number(avgScore, 1) })}</strong>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label htmlFor="roi-rec" style={LABEL}>{t('sh.roi.recommendationLabel')}</label>
            <select id="roi-rec" value={recommendation} onChange={(e) => setRecommendation(e.target.value)}
                    data-testid="select-recommendation"
                    style={{ padding: 8, borderRadius: 4, border: '1px solid var(--color-border-default)' }}>
              {RECOMMENDATIONS.map((r) => <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>)}
            </select>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label htmlFor="roi-remarks" style={LABEL}>{t('sh.roi.assessmentRemarks')}</label>
            <textarea id="roi-remarks" rows={3} value={remarks} onChange={(e) => setRemarks(e.target.value)}
                      data-testid="input-roi-remarks"
                      style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid var(--color-border-default)', resize: 'vertical' }} />
          </div>

          <Button type="submit" variant="primary" disabled={busy} data-testid="btn-submit-roi">
            {existing ? t('sh.roi.updateAssessment') : t('sh.roi.submitAssessment')}
          </Button>
        </form>
      </section>
    </main>
  );
}
